import { useEffect, useRef, useState } from "react";
import { FormFieldProps, useDispose, useFormField, useMemoizedFunction, useRefInitCallback } from "zeta-dom-react";
import { combineFn, setImmediate, setInterval, setTimeout } from "zeta-dom/util";
import dom from "zeta-dom/dom";
import config from "src/config";
import { useTranslation } from "src/i18n";
import { getValueOrDefault, iff, parseDuration } from "src/util/common";
import { OTPMode, OTPModes } from "src/core/constants";
import { StatusBar } from "src/components/common";
import { Button, validators } from ".";

const otpDigitCount = config.otpDigitCount;
const otpTimeout = parseDuration(config.otpTimeout, 's');
const otpResendInterval = parseDuration(config.otpResendInterval, 's');

export interface OTPFieldProps extends FormFieldProps<string> {
    allowSMS?: boolean;
    message?: string;
    showResendButton?: boolean;
    onRequestOTP: (mode: OTPMode) => any;
    onValidateSuccess?: () => any;
}

export interface OTPResendTimer {
    startTimer(): void;
}

export interface OTPResendButtonProps {
    timerRef: React.MutableRefObject<OTPResendTimer | undefined>;
    onClick: () => any;
    onEnableStateChanged: (enabled: boolean) => any;
}

export function OTPResendButton(props: OTPResendButtonProps) {
    const { t } = useTranslation('common');
    const [counter, setCounter] = useState(0);
    const dispose = useDispose();
    const onEnableStateChanged = useMemoizedFunction(props.onEnableStateChanged);

    const startTimer = useMemoizedFunction(() => {
        if (!counter) {
            dispose.push(setInterval(() => {
                setCounter(v => {
                    if (!--v) {
                        dispose();
                        setImmediate(() => {
                            onEnableStateChanged(true);
                        });
                    }
                    return v;
                });
            }, 1000));
        }
        setCounter(otpResendInterval);
        onEnableStateChanged(false);
    });
    props.timerRef.current = { startTimer };

    useEffect(() => {
        startTimer();
    }, [startTimer]);

    return (
        <Button variant="link" label={counter ? t('otp.resend.countdown', { counter }) : t('otp.resend')} disabled={!!counter}
            onClick={onClick} />
    );

    function onClick() {
        props.onClick();
        startTimer();
    }
}

export function OTPField(props: OTPFieldProps) {
    props = {
        ...props,
        validateOnChange: false,
        onValidate: validators.combine(v => v.length !== otpDigitCount ? 'Incorrect code' : '', props.onValidate)
    };
    const { t } = useTranslation('common');
    const [mode, setMode] = useState<OTPMode>(OTPModes.email);
    const [maskLength, setMaskLength] = useState(0);
    const [allowResend, setAllowResend] = useState(false);
    const [allowSwitchMode, setAllowSwitchMode] = useState(true);
    const timerRef = useRef<OTPResendTimer>();
    const onDispose = useDispose();
    const onRequestOTP = useMemoizedFunction(props.onRequestOTP);
    const onValidateSuccess = useMemoizedFunction(props.onValidateSuccess);
    const { value, error, setValue, elementRef, form } = useFormField(props, '');
    const [showError, setShowError] = useState(false);
    const [showReminder, setShowReminder] = useState(false);

    useEffect(() => {
        return form?.on('validationChange', e => {
            if (e.name === props.name) {
                if (!e.isValid) {
                    setValue('');
                    setShowError(true);
                } else {
                    onValidateSuccess();
                }
            }
        });
    }, [form, props.name, setValue, onValidateSuccess]);

    useEffect(() => {
        if (value.length && showError) {
            setShowError(false);
        }
    }, [value, showError]);

    return (
        <div ref={elementRef} className="eac-field eac-otp-field">
            {iff(showReminder, () =>
                <StatusBar variant="info">{t('otp.reminder')}</StatusBar>)}
            {iff(!props.message,
                () => <p>{t(`otp.instruction.${mode}`, { digits: otpDigitCount, timeout: otpTimeout })}</p>,
                () => <p>{props.message}</p>)}
            <div className="eac-otp-field-input" ref={combineFn(setFocus, useRefInitCallback(initEvent))}>
                {
                    '1'.repeat(otpDigitCount).split('').map((_, i) => (
                        <input key={i} type={i < maskLength ? 'password' : 'text'} value={getValueOrDefault(value[i], '')} className="eac-text-field"
                            autoComplete="one-time-code" autoCorrect="off" autoCapitalize="off" inputMode="numeric" onChange={() => void 0} />
                    ))
                }
                {iff(props.showResendButton !== false, () =>
                    <OTPResendButton timerRef={timerRef} onClick={resend} onEnableStateChanged={setAllowResend} />)}
            </div>
            {iff(error && showError, () =>
                <div className="eac-field-error">{error}</div>)}
            {iff(props.allowSMS, () =>
                <div className="eac-otp-field-aux">
                    <Button variant="link" label={mode === OTPModes.sms ? t('otp.resend.email') : t('otp.resend.sms')} disabled={!allowSwitchMode && !allowResend}
                        onClick={onClickResend} />
                </div>)}
        </div>
    );

    function onClickResend() {
        setShowReminder(true);
        resend(mode === OTPModes.sms ? OTPModes.email : OTPModes.sms);
        setAllowSwitchMode(false);
        timerRef.current?.startTimer();
    }

    function resend(forceMode?: OTPMode) {
        if (forceMode) {
            setMode(forceMode);
        }
        setValue('');
        setShowError(false);
        setMaskLength(0);
        onRequestOTP(getValueOrDefault(forceMode, mode));
    }

    function setFocus(element: HTMLElement | null) {
        if (element && dom.focused(element)) {
            let input = [...element.querySelectorAll('input')].find(v => !v.value);
            if (input) {
                dom.focus(input);
            }
        }
    }

    function initEvent(element: HTMLElement) {
        dom.on(element, 'focusin', () => {
            setFocus(element);
        });
        dom.on(element, 'textInput', e => {
            setValue(value1 => {
                const newValue = (value1 + e.data.replace(/\D/g, '')).slice(0, otpDigitCount);

                // mask previous entered number immediately
                // and show the latest entered number for 1 second
                setMaskLength(value1.length);
                onDispose.push(setTimeout(() => {
                    setMaskLength(v => Math.max(v, newValue.length));
                }, 1000));
                return newValue;
            });
            e.handled();
        });
        dom.on(element, 'backspace', e => {
            setValue(value1 => {
                return value1.slice(0, -1);
            });
            e.handled();
        });
        [...element.querySelectorAll('input')].forEach(v => {
            dom.on(v, 'focusin', () => {
                setFocus(element);
            });
        });
    }
}
